<template>
  <div v-loading="loading" class="page">
    <el-row :gutter="12" class="mb-16px">
      <el-col :xs="12" :md="6" v-for="item in kpis" :key="item.label">
        <div class="kpi">
          <div class="kpi__value">{{ item.value }}</div>
          <div class="kpi__label">{{ item.label }}</div>
        </div>
      </el-col>
    </el-row>

    <el-card shadow="never" class="panel">
      <template #header>
        <div class="panel-head">
          <div class="filters">
            <el-select v-model="period" clearable placeholder="全部账期" class="!w-140px" @change="noop">
              <el-option v-for="p in periodOptions" :key="p" :label="p" :value="p" />
            </el-select>
            <el-select v-model="settledFilter" clearable placeholder="归集状态" class="!w-130px">
              <el-option label="已归集" :value="1" />
              <el-option label="未归集" :value="0" />
            </el-select>
          </div>
          <el-button type="primary" plain :loading="exporting" @click="exportExcel">
            <Icon icon="ep:download" class="mr-4px" />导出 Excel
          </el-button>
        </div>
      </template>
      <el-table :data="filteredList" stripe>
        <el-table-column label="账期" prop="period" width="100" />
        <el-table-column label="软件" min-width="120">
          <template #default="{ row }">{{ row.softwareName || row.softwareId || '-' }}</template>
        </el-table-column>
        <el-table-column label="商家ID" prop="merchantId" width="100" />
        <el-table-column label="业务" prop="bizType" width="120" />
        <el-table-column label="基数(元)" width="110">
          <template #default="{ row }">¥{{ fen2yuan(row.basisAmount) }}</template>
        </el-table-column>
        <el-table-column label="比例" width="90">
          <template #default="{ row }">{{ ((row.rate || 0) * 100).toFixed(2) }}%</template>
        </el-table-column>
        <el-table-column label="分润(元)" width="110">
          <template #default="{ row }">
            <span class="profit">¥{{ fen2yuan(row.amount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="归集" width="90">
          <template #default="{ row }">
            <el-tag size="small" :type="row.settled ? 'success' : 'warning'">
              {{ row.settled ? '已归集' : '未归集' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="时间" prop="createTime" min-width="170" />
      </el-table>
      <el-empty v-if="!filteredList.length" description="暂无分润明细" :image-size="72" />
    </el-card>
  </div>
</template>

<script setup lang="ts">
import { exportPortalLedgers, getPortalLedgers } from '@/api/agent/portal'
import download from '@/utils/download'

defineOptions({ name: 'AgentPortalLedger' })

const loading = ref(false)
const exporting = ref(false)
const list = ref<any[]>([])
const period = ref<string>()
const settledFilter = ref<number>()

const fen2yuan = (fen?: number) =>
  fen == null
    ? '0'
    : (Number(fen) / 100).toLocaleString('zh-CN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })

const periodOptions = computed(() =>
  [...new Set(list.value.map((i) => i.period).filter(Boolean))].sort().reverse()
)

const filteredList = computed(() =>
  list.value.filter((row) => {
    if (period.value && row.period !== period.value) return false
    if (settledFilter.value === 1 && !row.settled) return false
    if (settledFilter.value === 0 && row.settled) return false
    return true
  })
)

const kpis = computed(() => {
  const rows = filteredList.value
  const total = rows.reduce((s, i) => s + (i.amount || 0), 0)
  const unsettled = rows.filter((i) => !i.settled).reduce((s, i) => s + (i.amount || 0), 0)
  const settled = total - unsettled
  return [
    { label: '明细笔数', value: String(rows.length) },
    { label: '分润合计', value: `¥${fen2yuan(total)}` },
    { label: '已归集', value: `¥${fen2yuan(settled)}` },
    { label: '未归集', value: `¥${fen2yuan(unsettled)}` }
  ]
})

const noop = () => {}

const exportExcel = async () => {
  exporting.value = true
  try {
    const data = await exportPortalLedgers()
    download.excel(data, '分润明细.xls')
  } finally {
    exporting.value = false
  }
}

onMounted(async () => {
  loading.value = true
  try {
    list.value = (await getPortalLedgers()) || []
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.kpi {
  background: #fff;
  border: 1px solid var(--el-border-color-lighter);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 8px;
  min-height: 72px;
}
.kpi__value {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}
.kpi__label {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.filters {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.profit {
  color: #0e9c8d;
  font-weight: 700;
}
</style>
